import { FC, Suspense, useState } from 'react'
import { connect } from 'react-redux'
import Operations from '../../../operations'
import Selectors from '../../../selectors'
import { css, StyleSheet } from 'aphrodite/no-important'
import _ from 'lodash'
import Modal from '..'
import Field from '../../fields'
import { showToast } from '../../../common/utils'
import { ScrollLock } from '../../../LEGACY/elemental'
import { Step, StepList } from '../../generic'
import {
  ModalCategorySelector,
  ModalCategoryItemSelector,
  ModalResidentSelector,
  ModalTitleSelector,
} from '../components'
import LogsGoalsSelect from './LogsGoalsSelect'
import { LogCategory, Resident } from '@lmc/ts-common'
import useFeatureFlags from 'hooks/useFeatureFlags'
import LogsSeizureDeEscalation from './LogsSeizureDeEscalation'

import {
  ITEM_ID_SEIZURE,
  ITEM_ID_SHORT_SEIZURE,
  INCIDENT_CATEGORY_ID,
  HR_CATEGORY_ID,
} from '@lmc/common/types'

interface AddLogModalProps {
  categories: LogCategory[]
  categoryItems: any[]
  close: () => void
  createItem: (args: { url: string; json: any; onComplete: () => void, onError: () => void }) => void
  fetchLogs: () => void
  fields: any // Add the appropriate type
  hasPro: boolean
  isOpen: boolean
  residents: Resident[]
  summary: {
    icon: {
      color: string
      url: string
    }
    text: string
  }
  setFormField: (args: { key: string; value: any }) => void
}

const AddLogModal: FC<AddLogModalProps> = ({
  categories,
  categoryItems,
  close,
  hasPro,
  isOpen,
  residents,
  summary,
  ...formProps
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const flags = useFeatureFlags({
    flagNames: ['clinical_safety_seizures'],
  })?.data

  const checkTitleValid = () => {
    if (!formProps.fields.title || !formProps.fields.description) return false
    return true
  }

  const filterResidentsByStatus = (residents: any[]) =>
    _.filter(residents, (resident) =>
      PERMITTED_RESIDENT_STATUSES.includes(resident.status)
    )

  const getHeaderProps = () => {
    const selectedResident = _.find(residents, {
      id: formProps.fields.resident,
    })
    if (!selectedResident) return null

    return {
      isMainHeader: true,
      profilePicture: selectedResident.picture,
      title: `${selectedResident.name.first} ${selectedResident.name.last}`,
    }
  }

  const handleSubmit = () => {
    setIsLoading(true)
	formProps.createItem({
		url: 'list/logs',
		json: formProps.fields,
		onComplete: () => {
			formProps.fetchLogs()
			showToast({ title: 'Added a Log' })
			setIsLoading(false)
		},
		onError: () => { setIsLoading(false) }
	})
  }

  const renderDateTimePicker = () => (
    <div className={css(classes.timePickerContainer)}>
      <div className={css(classes.formField)}>
        <label className={css(classes.label)}>{TIME}</label>
        <div className={css(classes.datePickerContainer)}>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              formId={MODAL_ID}
              keys="timeLogged"
              layout="column"
              type="datetime"
              values={{
                ...formProps.fields,
                timeLogged: formProps.fields.timeLogged || new Date(),
              }}
            />
          </Suspense>
        </div>
      </div>
    </div>
  )

  const renderFlagForHandover = () => {
    return hasPro ? (
      <div style={styles.handoverContainer}>
        <Suspense fallback={<div>Loading...</div>}>
          <Field
            formId={MODAL_ID}
            leftIcon="flag"
            keys="isNextHandover"
            label="Flag for handover?"
            type="singleCheckbox"
            values={formProps.fields}
          />
        </Suspense>
      </div>
    ) : null
  }

  if (
    !isOpen ||
    !categories ||
    !categoryItems ||
    !residents ||
    !summary ||
    !formProps.fields
  ) {
    return null
  }

  const {
    icon: { color, url },
    text,
  } = summary

  // CARE-693: filter out hidden categories and items
  const visibleCategoryItems = _.filter(categoryItems, (d) => {
    if (d.isHidden) {
      return false
    }
    if (
      d.id === ITEM_ID_SHORT_SEIZURE &&
      flags?.clinical_safety_seizures?.enabled
    ) {
      return false
    }
    return true
  })

  // hide category if all items hidden
  const visibleCategories = _.filter(categories, ({ id }) =>
    _.find(visibleCategoryItems, { category: id })
  )

  const { category } = formProps.fields
  const chosenItems = category && _.filter(visibleCategoryItems, { category })
  const chosenItem =
    formProps.fields &&
    formProps.fields.item &&
    _.find(visibleCategoryItems, { id: formProps.fields.item })
  const chosenResidents = filterResidentsByStatus(residents)
  const selectedResident = _.find(residents, {
    id: formProps.fields.resident,
  })

  const onChangeSeizureType = (deEscalated: boolean) => {
    const itemId = deEscalated ? ITEM_ID_SHORT_SEIZURE : ITEM_ID_SEIZURE
    const categoryId = deEscalated ? HR_CATEGORY_ID : INCIDENT_CATEGORY_ID
    const title = categoryItems?.find((item) => item?.id === itemId)?.name

    formProps.setFormField({
      key: 'item',
      value: itemId,
    })
    formProps.setFormField({
      key: 'category',
      value: categoryId,
    })
    formProps.setFormField({
      key: 'title',
      value: title,
    })
  }

  const isSeizure =
    formProps.fields.item === ITEM_ID_SEIZURE ||
    formProps.fields.item === ITEM_ID_SHORT_SEIZURE

  const showSeizureSwitch =
    flags?.clinical_safety_seizures?.enabled && isSeizure

  return (
    <div data-cy="create-log-item-container">
      <StepList
        backIconColor={color}
        backIcon={url}
        backText={text}
        closeLabel="Cancel"
        headerProps={getHeaderProps()}
        onClose={close}
      >
        <Step
          component={ModalResidentSelector}
          data={{ residents: chosenResidents }}
          {...formProps}
        />
        <Step
          component={ModalCategorySelector}
          allCategories
          data={{ categories: visibleCategories }}
          {...formProps}
        />
        <Step
          component={ModalCategoryItemSelector}
          data={{ items: chosenItems, categories: visibleCategories }}
          {...formProps}
        />
        <Step
          component={ModalTitleSelector}
          isValid={checkTitleValid}
          onSubmit={handleSubmit}
          formId={MODAL_ID}
          submitButtonText="Add Log"
          buttonGlyph="plus"
          item={chosenItem}
          validationText="Title and Description is required"
          stepHeader={renderDateTimePicker()}
          stepFooter={
            <>
              <LogsGoalsSelect
                residentID={selectedResident?.id}
                onChange={(value) =>
                  formProps.setFormField({
                    key: 'goals',
                    value,
                  })
                }
              />
              {showSeizureSwitch && (
                <LogsSeizureDeEscalation
                  deEscalated={formProps.fields.item === ITEM_ID_SHORT_SEIZURE}
                  onChange={onChangeSeizureType}
                />
              )}
              {renderFlagForHandover()}
            </>
          }
          selectedResident={selectedResident}
          isLoading={isLoading}
          {...formProps}
        />
      </StepList>
      <ScrollLock />
    </div>
  )
}

const TIME = 'Time'
const PERMITTED_RESIDENT_STATUSES = [
  'active',
  'hospitalised',
  'released',
  'dummy',
]
const MODAL_ID = 'addLog'

const styles = {
  handoverContainer: {
    margin: 'auto',
    maxWidth: 400,
  },
}

const classes = StyleSheet.create({
  timePickerContainer: {
    maxWidth: 400,
    margin: 'auto',
  },
  label: {
    textAlign: 'left',
    fontWeight: 600,
  },
  formField: {
    paddingBottom: 15,
    maxWidth: 400,
  },
  datePickerContainer: {
    marginTop: 4,
  },
})

const mapStateToProps = (state: any, props: any) => ({
  categories: state.data['log-categories'],
  categoryItems: state.data['log-category-items'],
  hasPro: _.get(state, 'global.home.pro'),
  residents: state.data.residents,
  // @ts-ignore
  summary: Selectors.getTodoSummary(state, props),
})

const mapDispatchToProps = (dispatch: any) => ({
  fetchLogs: () =>
    dispatch(Operations.fetchLogs({ search: '', timeLogged: {} })),
})

const wrappedModal = connect(mapStateToProps, mapDispatchToProps)(AddLogModal)

export default Modal(wrappedModal, MODAL_ID)
